import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { auth } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';

/**
 * HQ login screen. Restricted to admin users — access is enforced server-side
 * by the `hqAuthMiddleware` which checks the authenticated email against the
 * `ADMIN_EMAILS` environment variable.
 *
 * Redirects to `/hq/dashboard` immediately if the user is already authenticated.
 */
export default function HQLogin() {
    const navigate = useNavigate();
    const { currentUser, authError } = useAuth();

    const [localError, setLocalError] = useState('');
    const [loading, setLoading] = useState(false);

    const error = localError || authError;

    // If already logged in, redirect to dashboard
    useEffect(() => {
        if (currentUser) {
            navigate('/hq/dashboard');
        }
    }, [currentUser, navigate]);

    /**
     * Initiates Google OAuth sign-in via a popup.
     * Forces account selection so admins can switch Google accounts if needed.
     * Navigation after success is handled by the `useEffect` above once
     * `AuthContext` updates `currentUser`.
     */
    const handleGoogleLogin = async () => {
        setLocalError('');
        setLoading(true);

        try {
            const provider = new GoogleAuthProvider();
            // Optional: Ask Google to prompt for account selection
            provider.setCustomParameters({ prompt: 'select_account' });
            await signInWithPopup(auth, provider);
            // Validation and navigation will be handled by AuthContext and useEffect
        } catch (err: any) {
            console.error('HQLogin.handleGoogleLogin Error:', err);
            setLocalError(err.message || 'Failed to sign in with Google');
            setLoading(false);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-zinc-950 flex flex-col items-center justify-center text-zinc-100 p-4">
            <div className="max-w-md w-full bg-zinc-900 border border-zinc-800 rounded-2xl p-8 shadow-2xl">
                <h2 className="text-3xl font-bold text-center mb-2">Welcome back</h2>
                <p className="text-zinc-400 text-center mb-8">Sign in to access your HQ analytics dashboard.</p>

                {error && (
                    <div className="bg-red-500/10 border border-red-500/50 text-red-400 p-3 rounded-xl text-sm mb-6">
                        {error}
                    </div>
                )}

                <button
                    onClick={handleGoogleLogin}
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-3 bg-zinc-100 text-zinc-950 font-bold py-3 px-4 rounded-xl hover:bg-white transition-colors disabled:opacity-50"
                >
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                    </svg>
                    {loading ? 'Signing in...' : 'Sign in with Google'}
                </button>

                <div className="mt-8 flex justify-center gap-4 text-sm text-zinc-500">
                    <Link to="/privacy-policy" className="hover:text-yellow-400 transition-colors">Privacy Policy</Link>
                    <span>·</span>
                    <a href="#" className="hover:text-yellow-400 transition-colors">Terms of Service</a>
                </div>
            </div>
        </div>
    );
}
